import SegmentedButton from "@/components/SegmentedButton";
import classNames from "classnames";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import type { PeriodMode, PeriodWindow } from "../period";
import { currentPeriod, filmstripLabel, isCurrentPeriod, neighboringPeriods } from "../period";

const MODE_OPTIONS = [
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Year", value: "ytd" }
];

const PILL_CLASS =
  "shrink-0 min-w-fit inline-flex items-center justify-center h-10 min-h-[40px] rounded-full px-3 sm:px-4 text-label-large border transition-colors whitespace-nowrap";

type Props = {
  period: PeriodWindow;
  onModeChange: (mode: PeriodMode) => void;
  onSelectNeighbor: (period: PeriodWindow) => void;
};

function PeriodPill({
  label,
  selected,
  ariaLabel,
  onClick
}: {
  label: string;
  selected: boolean;
  ariaLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={ariaLabel}
      onClick={onClick}
      className={classNames(
        PILL_CLASS,
        selected
          ? "bg-sys-brand-secondary-container text-sys-brand-on-secondary-container border-transparent"
          : "bg-sys-brand-surface text-sys-brand-on-surface-variant border-sys-brand-outline-variant"
      )}
    >
      {label}
    </button>
  );
}

export default function PeriodScroller({ period, onModeChange, onSelectNeighbor }: Props) {
  const { isCompactLayout, isMobile } = useIsCompactLayout();
  const labelDensity = isMobile ? "narrow" : isCompactLayout ? "compact" : "full";
  // Year pills are short — always show two prior years. Week/month stay leaner on compact.
  const neighborCount = period.mode === "ytd" ? 2 : isMobile || isCompactLayout ? 1 : 2;
  const neighbors = neighboringPeriods(period, neighborCount).filter((neighbor) => !isCurrentPeriod(neighbor));
  const viewingCurrent = isCurrentPeriod(period);
  const current = currentPeriod(period.mode);
  const currentPillLabel = filmstripLabel(current, labelDensity);

  const modeControl = (
    <SegmentedButton
      ariaLabel="Period mode"
      options={MODE_OPTIONS}
      value={period.mode}
      fullWidth={isCompactLayout}
      onChange={(value) => {
        if (value === "week" || value === "month" || value === "ytd") onModeChange(value);
      }}
    />
  );

  const filmstrip = (
    <div
      className="flex min-w-0 flex-1 items-center justify-end gap-2 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      data-testid="roster-activity-period-filmstrip"
      role="group"
      aria-label={period.mode === "ytd" ? "Year" : "Period"}
    >
      {neighbors.map((neighbor) => {
        const selected = neighbor.anchor.hasSame(
          period.anchor,
          period.mode === "week" ? "week" : period.mode === "month" ? "month" : "year"
        );
        return (
          <PeriodPill
            key={`${neighbor.mode}-${neighbor.anchor.toISODate()}`}
            label={filmstripLabel(neighbor, labelDensity)}
            selected={selected}
            ariaLabel={neighbor.label}
            onClick={() => onSelectNeighbor(neighbor)}
          />
        );
      })}
    </div>
  );

  const currentPill = (
    <div className="flex shrink-0 items-center gap-2">
      <div className="h-6 w-px bg-sys-brand-outline-variant" aria-hidden="true" />
      <PeriodPill
        label={currentPillLabel}
        selected={viewingCurrent}
        ariaLabel={`Jump to current period: ${current.label}`}
        onClick={() => onSelectNeighbor(current)}
      />
    </div>
  );

  const periodRow = (
    <div className="flex min-w-0 w-full items-center gap-2">
      {filmstrip}
      {currentPill}
    </div>
  );

  if (isMobile || isCompactLayout) {
    return (
      <div className="flex flex-col gap-3" data-testid="roster-activity-period-scroller">
        {modeControl}
        {periodRow}
        <p className="sr-only" aria-live="polite">
          {period.label}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid="roster-activity-period-scroller">
      <div className="shrink-0">{modeControl}</div>
      <div className="flex min-w-0 flex-1 items-center justify-end">{periodRow}</div>
      <p className="sr-only" aria-live="polite">
        {period.label}
      </p>
    </div>
  );
}
